import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trackConversion } from "@/lib/tracking";

const businessTypes = [
  "Office / Professional",
  "Retail / Storefront",
  "Restaurant / Hospitality",
  "Warehouse / Distribution",
  "Manufacturing",
  "Healthcare",
  "Education",
  "Government",
  "Other",
];

export default function QuoteForm() {
  const [showUpload, setShowUpload] = useState(false);
  const [uploadState, setUploadState] = useState<"idle" | "uploading" | "done">("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedEmail, setUploadedEmail] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    businessName: "",
    contactName: "",
    email: "",
    phone: "",
    monthlyKwh: "",
    businessType: "",
    zipCode: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    trackConversion("form_submission", {
      business_type: formData.businessType,
      zip: formData.zipCode,
    });
    alert("Quote request submitted. We will contact you within 24 hours.");
  };

  const simulateUpload = useCallback(() => {
    setUploadState("uploading");
    setUploadProgress(0);
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 20 + 5;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setUploadState("done");
        trackConversion("bill_upload");
      }
      setUploadProgress(Math.min(progress, 100));
    }, 200);
  }, []);

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer.files.length > 0) {
        simulateUpload();
      }
    },
    [simulateUpload]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        simulateUpload();
      }
    },
    [simulateUpload]
  );

  if (showUpload) {
    return (
      <div className="bg-card border border-border p-6">
        <h2 className="font-display font-semibold text-lg mb-4 text-foreground">Upload Your Bill</h2>

        {uploadState === "idle" && (
          <>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleFileDrop}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-border hover:border-foreground transition-colors cursor-pointer flex items-center justify-center min-h-[200px] p-8"
            >
              <p className="text-sm text-muted-foreground font-body text-center">
                Drag your PDF, JPG, or PNG here. We will parse the details.
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={handleFileSelect}
            />
            <button
              onClick={() => setShowUpload(false)}
              className="mt-4 text-xs text-muted-foreground font-body hover:text-foreground transition-colors"
            >
              Back to form
            </button>
          </>
        )}

        {uploadState === "uploading" && (
          <div className="min-h-[200px] flex flex-col items-center justify-center gap-4">
            <div className="progress-track w-full max-w-xs">
              <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
            <p className="text-xs text-muted-foreground font-body">
              Uploading… {Math.round(uploadProgress)}%
            </p>
          </div>
        )}

        {uploadState === "done" && (
          <div className="min-h-[200px] flex flex-col items-center justify-center gap-4">
            <p className="text-sm font-body text-foreground text-center">
              Bill received.
            </p>
            {!uploadedEmail ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (uploadedEmail) {
                    trackConversion("bill_upload", { email: uploadedEmail });
                  }
                }}
                className="flex flex-col gap-2 w-full max-w-xs"
              >
                <Label htmlFor="upload-email" className="text-xs font-body text-muted-foreground">
                  Enter your email for the quote
                </Label>
                <Input
                  id="upload-email"
                  type="email"
                  required
                  value={uploadedEmail}
                  onChange={(e) => setUploadedEmail(e.target.value)}
                  className="font-body text-sm"
                  placeholder="you@company.com"
                />
                <Button variant="action" size="sm" type="submit">
                  Send My Quote
                </Button>
              </form>
            ) : (
              <p className="text-xs text-muted-foreground font-body">
                A quote will be sent to {uploadedEmail} within 24 hours.
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-card border border-border p-6">
      <h2 className="font-display font-semibold text-lg mb-4 text-foreground">Get a Quote</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <Label htmlFor="businessName" className="text-xs font-body text-muted-foreground">Business Name</Label>
          <Input id="businessName" name="businessName" required value={formData.businessName} onChange={handleChange} className="font-body text-sm mt-1" />
        </div>
        <div>
          <Label htmlFor="contactName" className="text-xs font-body text-muted-foreground">Contact Name</Label>
          <Input id="contactName" name="contactName" required value={formData.contactName} onChange={handleChange} className="font-body text-sm mt-1" />
        </div>
        <div>
          <Label htmlFor="email" className="text-xs font-body text-muted-foreground">Email</Label>
          <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className="font-body text-sm mt-1" />
        </div>
        <div>
          <Label htmlFor="phone" className="text-xs font-body text-muted-foreground">Phone</Label>
          <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleChange} className="font-body text-sm mt-1" />
        </div>
        <div>
          <Label htmlFor="monthlyKwh" className="text-xs font-body text-muted-foreground">Monthly kWh (or upload bill below)</Label>
          <Input id="monthlyKwh" name="monthlyKwh" value={formData.monthlyKwh} onChange={handleChange} className="font-body text-sm mt-1" placeholder="e.g. 50,000" />
        </div>
        <div>
          <Label htmlFor="businessType" className="text-xs font-body text-muted-foreground">Business Type</Label>
          <select
            id="businessType"
            name="businessType"
            required
            value={formData.businessType}
            onChange={handleChange}
            className="flex h-10 w-full rounded-sm border border-input bg-background px-3 py-2 text-sm font-body ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring mt-1"
          >
            <option value="">Select…</option>
            {businessTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="zipCode" className="text-xs font-body text-muted-foreground">Service Address / ZIP Code</Label>
          <Input id="zipCode" name="zipCode" required value={formData.zipCode} onChange={handleChange} className="font-body text-sm mt-1" placeholder="e.g. 10001" />
        </div>

        <Button variant="action" size="lg" type="submit" className="w-full mt-2">
          Get a Quote
        </Button>
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="w-full font-display font-semibold uppercase text-xs tracking-wide"
          onClick={() => setShowUpload(true)}
        >
          Upload Your Bill
        </Button>
      </form>
    </div>
  );
}
